// Admin — Purchase Orders (list → draft → send → receive)
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGate from '../../components/PermissionGate';
import { Search, ArrowLeft, Plus, ChevronRight, Trash2, FileText, Send, XCircle, CheckCircle, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: 'bg-gray-500/20 text-gray-400',
  sent: 'bg-blue-500/20 text-blue-400',
  partial_received: 'bg-amber-500/20 text-amber-400',
  received: 'bg-green-500/20 text-green-400',
  short_closed: 'bg-orange-500/20 text-orange-400',
  cancelled: 'bg-red-500/20 text-red-400',
};

export default function AdminPurchaseOrders() {
  const { token } = useAuth();
  const [list, setList] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites((d.items || d || []).filter(s => s.site_type !== 'warehouse')));
    const q = new URLSearchParams(window.location.search);
    const open = q.get('open');
    if (open) setSelected({ id: open });
  }, []);

  const fetchList = async () => {
    setLoading(true);
    const p = new URLSearchParams({ page, page_size: 50 });
    if (status) p.append('status', status);
    if (siteId) p.append('site_id', siteId);
    if (search) p.append('search', search);
    const res = await fetch(`${API}/api/purchasing/purchase-orders?${p}`, { headers });
    setList(await res.json());
    setLoading(false);
  };
  useEffect(() => { if (!selected) fetchList(); }, [status, search, siteId, page, selected]);

  if (selected) return <POCard id={selected.id} onBack={() => { setSelected(null); fetchList(); }} token={token} />;

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Purchase Orders</h1>
          <p className="text-uf-textMuted text-sm mt-1">{list.total?.toLocaleString() || 0} POs</p>
        </div>
        <PermissionGate perm="purchasing.create_po">
          <button onClick={() => setSelected({ id: 'new' })} data-testid="new-po-btn" className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> New PO</button>
        </PermissionGate>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-uf-textMuted" />
          <input placeholder="Search PO number..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} data-testid="po-search" className="w-full bg-uf-surface border border-uf-border pl-10 pr-4 py-2 text-white text-sm rounded" />
        </div>
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} data-testid="po-status-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="sent">Sent (In Transit)</option>
          <option value="partial_received">Partial Received</option>
          <option value="received">Received</option>
          <option value="short_closed">Short Closed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={siteId} onChange={e => { setSiteId(e.target.value); setPage(1); }} data-testid="po-site-filter" className="bg-uf-surface border border-uf-border px-3 py-2 text-white text-sm rounded">
          <option value="">All sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      <div className="bg-uf-surface border border-uf-border rounded overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-uf-bg text-uf-textMuted text-xs uppercase tracking-wider font-bold">
          <div className="col-span-2">PO #</div>
          <div className="col-span-3">Supplier</div>
          <div className="col-span-2">Delivery site</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Expected</div>
          <div className="col-span-1 text-right">Total £</div>
          <div className="col-span-1"></div>
        </div>
        {loading && <div className="p-8 text-center text-uf-textMuted">Loading…</div>}
        {!loading && list.items?.length === 0 && <div className="p-8 text-center text-uf-textMuted">No POs match</div>}
        {list.items?.map(po =>
          <div key={po.id} onClick={() => setSelected(po)} data-testid={`po-row-${po.id}`} className="grid grid-cols-12 gap-2 px-4 py-3 border-t border-uf-border hover:bg-uf-bg cursor-pointer items-center">
            <div className="col-span-2 text-white font-mono text-sm">{po.po_number}</div>
            <div className="col-span-3 text-white text-sm truncate">{po.supplier_name}</div>
            <div className="col-span-2 text-uf-textMuted text-sm truncate">{po.site_name}</div>
            <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_STYLES[po.status]}`}>{po.status?.replace('_', ' ')}</span></div>
            <div className="col-span-1 text-uf-textMuted text-xs">{po.expected_date || '—'}</div>
            <div className="col-span-1 text-right text-white">£{po.total_cost?.toFixed(2)}</div>
            <div className="col-span-1 text-right"><ChevronRight className="w-4 h-4 text-uf-textMuted inline" /></div>
          </div>
        )}
      </div>
    </div>
  );
}

function POCard({ id, onBack, token }) {
  const [po, setPo] = useState(id === 'new' ? null : null);
  const [mode, setMode] = useState(id === 'new' ? 'draft' : 'view'); // draft | view | receive
  const [suppliers, setSuppliers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ supplier_id: '', site_id: '', expected_date: '', notes: '', items: [] });
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [grnLines, setGrnLines] = useState({});  // po_line_id -> {qty, discrepancy_type, discrepancy_reason}
  const [grnNotes, setGrnNotes] = useState('');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (id === 'new') {
      fetch(`${API}/api/purchasing/suppliers`, { headers }).then(r => r.json()).then(d => setSuppliers(d.items || []));
      fetch(`${API}/api/sites`, { headers }).then(r => r.json()).then(d => setSites((d.items || d || []).filter(s => s.site_type !== 'warehouse')));
    } else {
      fetch(`${API}/api/purchasing/purchase-orders/${id}`, { headers }).then(r => r.json()).then(setPo);
    }
  }, [id]);

  useEffect(() => {
    if (!productSearch) { setProductResults([]); return; }
    const t = setTimeout(() => {
      fetch(`${API}/api/products?search=${encodeURIComponent(productSearch)}&page_size=10`, { headers })
        .then(r => r.json()).then(d => setProductResults(d.items || []));
    }, 200);
    return () => clearTimeout(t);
  }, [productSearch]);

  const addItem = (p) => {
    setForm(f => ({ ...f, items: [...f.items, { product_id: p.id, product_name: p.name, product_sku: p.sku, qty_ordered: 1, unit_cost: p.cost_price || p.price, description: '' }] }));
    setProductSearch('');
    setProductResults([]);
  };
  const updItem = (i, key, val) => setForm(f => ({ ...f, items: f.items.map((it, ix) => ix === i ? { ...it, [key]: val } : it) }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, ix) => ix !== i) }));

  const savePO = async () => {
    if (!form.supplier_id || !form.site_id || form.items.length === 0) { alert('Need supplier, site, and at least one line'); return; }
    const res = await fetch(`${API}/api/purchasing/purchase-orders`, { method: 'POST', headers, body: JSON.stringify(form) });
    const d = await res.json();
    if (res.ok) { setPo(d); setMode('view'); }
    else alert(d.detail || 'Save failed');
  };

  const sendPO = async () => {
    if (!confirm('Send PO to supplier? This marks it as ordered.')) return;
    const res = await fetch(`${API}/api/purchasing/purchase-orders/${po.id}/send`, { method: 'POST', headers });
    const d = await res.json();
    if (res.ok) setPo(d);
    else alert(d.detail || 'Send failed');
  };
  const cancelPO = async () => {
    const reason = prompt('Reason for cancelling this PO?');
    if (!reason) return;
    const res = await fetch(`${API}/api/purchasing/purchase-orders/${po.id}/cancel`, { method: 'POST', headers, body: JSON.stringify({ reason }) });
    const d = await res.json();
    if (res.ok) setPo(d);
    else alert(d.detail || 'Cancel failed');
  };
  const downloadPDF = async (id, poNum) => {
    const res = await fetch(`${API}/api/purchasing/purchase-orders/${id}/pdf`, { headers });
    if (!res.ok) { alert('Could not download PDF'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${poNum}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const startReceive = () => {
    const init = {};
    po.lines.forEach(l => { if (l.status === 'open') init[l.id] = { qty: l.qty_outstanding, discrepancy_type: 'correct', discrepancy_reason: '' }; });
    setGrnLines(init);
    setGrnNotes('');
    setMode('receive');
  };
  const postGRN = async () => {
    const lines = Object.entries(grnLines).filter(([_, v]) => v.qty > 0).map(([po_line_id, v]) => ({
      po_line_id, qty_received: parseInt(v.qty), discrepancy_type: v.discrepancy_type, discrepancy_reason: v.discrepancy_reason,
    }));
    if (lines.length === 0) { alert('Set qty on at least one line'); return; }
    const res = await fetch(`${API}/api/purchasing/purchase-orders/${po.id}/grn`, { method: 'POST', headers, body: JSON.stringify({ notes: grnNotes, lines }) });
    const d = await res.json();
    if (res.ok) {
      alert(`GRN ${d.grn_number} posted. PO status: ${d.po_status}.${d.credit_notes.length ? '\nCredit notes raised: ' + d.credit_notes.join(', ') : ''}`);
      // Reload PO
      const r2 = await fetch(`${API}/api/purchasing/purchase-orders/${po.id}`, { headers });
      setPo(await r2.json());
      setMode('view');
    } else alert(d.detail || 'GRN post failed');
  };

  const total = form.items.reduce((s, it) => s + (parseFloat(it.qty_ordered) || 0) * (parseFloat(it.unit_cost) || 0), 0);

  return (
    <div className="p-6 min-h-screen bg-uf-bg">
      <button onClick={onBack} data-testid="po-card-back" className="flex items-center gap-2 text-uf-textMuted hover:text-white mb-4 text-sm"><ArrowLeft className="w-4 h-4" /> Back to list</button>

      {/* NEW DRAFT */}
      {id === 'new' && mode === 'draft' && (
        <div className="max-w-4xl">
          <h1 className="font-heading text-3xl font-bold text-white mb-6">New Purchase Order</h1>
          <div className="bg-uf-surface border border-uf-border rounded p-6 mb-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div><label className="text-xs text-uf-textMuted block mb-1">Supplier</label>
                <select value={form.supplier_id} onChange={e => setForm({ ...form, supplier_id: e.target.value })} data-testid="po-supplier-select" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                  <option value="">Select supplier…</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <div><label className="text-xs text-uf-textMuted block mb-1">Delivery site</label>
                <select value={form.site_id} onChange={e => setForm({ ...form, site_id: e.target.value })} data-testid="po-site-select" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded">
                  <option value="">Select site…</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <div><label className="text-xs text-uf-textMuted block mb-1">Expected date</label>
                <input type="date" value={form.expected_date} onChange={e => setForm({ ...form, expected_date: e.target.value })} data-testid="po-expected-date" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" /></div>
            </div>
            <div><label className="text-xs text-uf-textMuted block mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="po-notes" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" /></div>
          </div>

          <div className="bg-uf-surface border border-uf-border rounded p-6 mb-4">
            <h3 className="text-white font-bold mb-3">Line items</h3>
            <div className="relative mb-3">
              <input placeholder="Search product to add…" value={productSearch} onChange={e => setProductSearch(e.target.value)} data-testid="po-product-search" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded" />
              {productResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 bg-uf-surface border border-uf-border mt-1 rounded max-h-60 overflow-y-auto z-10">
                  {productResults.map(p => <div key={p.id} onClick={() => addItem(p)} data-testid={`po-add-${p.id}`} className="px-3 py-2 hover:bg-uf-bg cursor-pointer text-sm text-white flex justify-between"><span>{p.name}</span><span className="text-uf-textMuted">£{(p.cost_price || p.price)?.toFixed(2)}</span></div>)}
                </div>
              )}
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) =>
                <div key={i} className="grid grid-cols-12 gap-2 items-center text-sm">
                  <div className="col-span-5 text-white">{it.product_name}</div>
                  <input type="number" min="1" value={it.qty_ordered} onChange={e => updItem(i, 'qty_ordered', parseInt(e.target.value) || 0)} data-testid={`po-line-qty-${i}`} className="col-span-2 bg-uf-bg border border-uf-border px-2 py-1 text-white rounded text-right" />
                  <div className="col-span-1 text-uf-textMuted text-right">×</div>
                  <input type="number" step="0.01" value={it.unit_cost} onChange={e => updItem(i, 'unit_cost', parseFloat(e.target.value) || 0)} data-testid={`po-line-cost-${i}`} className="col-span-2 bg-uf-bg border border-uf-border px-2 py-1 text-white rounded text-right" />
                  <div className="col-span-1 text-white text-right">£{((it.qty_ordered || 0) * (it.unit_cost || 0)).toFixed(2)}</div>
                  <button onClick={() => rmItem(i)} className="col-span-1 text-uf-textMuted hover:text-red-400"><Trash2 className="w-4 h-4 mx-auto" /></button>
                </div>
              )}
              {form.items.length === 0 && <div className="text-uf-textMuted text-sm text-center py-4">No items yet — search for products above</div>}
            </div>
            <div className="border-t border-uf-border mt-4 pt-3 flex justify-end">
              <div className="text-right"><div className="text-xs text-uf-textMuted">Total</div><div className="text-2xl font-heading font-bold text-white">£{total.toFixed(2)}</div></div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={onBack} className="px-4 py-2 border border-uf-border text-white rounded">Cancel</button>
            <button onClick={savePO} data-testid="po-save-draft" className="px-4 py-2 bg-uf-red text-white rounded flex items-center gap-2"><FileText className="w-4 h-4" /> Save as Draft</button>
          </div>
        </div>
      )}

      {/* VIEW / RECEIVE */}
      {po && mode !== 'draft' && (
        <div className="max-w-5xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-uf-textMuted text-xs uppercase tracking-wider">{po.supplier_name}</div>
              <h1 className="font-heading text-3xl font-bold text-white">{po.po_number}</h1>
              <div className="flex items-center gap-2 mt-1 text-sm">
                <span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_STYLES[po.status]}`}>{po.status?.replace('_', ' ')}</span>
                <span className="text-uf-textMuted">Deliver to {po.site_name}</span>
                {po.expected_date && <span className="text-uf-textMuted">· Expected {po.expected_date}</span>}
              </div>
            </div>
            <div className="text-right"><div className="text-xs text-uf-textMuted">Total</div><div className="text-3xl font-heading font-bold text-uf-red">£{po.total_cost?.toFixed(2)}</div></div>
          </div>

          {/* Action bar */}
          {mode === 'view' && (
            <div className="flex gap-2 mb-4">
              {po.status === 'draft' && <PermissionGate perm="purchasing.send_po"><button onClick={sendPO} data-testid="po-send-btn" className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-1"><Send className="w-3 h-3" /> Send to Supplier</button></PermissionGate>}
              {(po.status === 'sent' || po.status === 'partial_received') && <PermissionGate perm="purchasing.receive_goods"><button onClick={startReceive} data-testid="po-receive-btn" className="px-3 py-2 bg-green-600 text-white rounded text-sm flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Receive Goods</button></PermissionGate>}
              {['draft', 'sent', 'partial_received'].includes(po.status) && <PermissionGate perm="purchasing.create_po"><button onClick={cancelPO} data-testid="po-cancel-btn" className="px-3 py-2 border border-red-500/50 text-red-400 rounded text-sm flex items-center gap-1"><XCircle className="w-3 h-3" /> Cancel PO</button></PermissionGate>}
              <button onClick={() => downloadPDF(po.id, po.po_number)} data-testid="po-download-pdf" className="px-3 py-2 bg-uf-red text-white rounded text-sm flex items-center gap-1"><FileText className="w-3 h-3" /> Download PDF</button>
              <a href={`/admin/purchase-orders/${po.id}/print`} target="_blank" rel="noreferrer" className="px-3 py-2 border border-uf-border text-white rounded text-sm flex items-center gap-1"><FileText className="w-3 h-3" /> Preview</a>
            </div>
          )}

          {/* Lines */}
          <div className="bg-uf-surface border border-uf-border rounded p-6">
            <h3 className="text-white font-bold mb-3">{mode === 'receive' ? 'Receive goods' : 'Lines'}</h3>
            {mode === 'receive' && <textarea placeholder="Delivery note / van number / notes..." value={grnNotes} onChange={e => setGrnNotes(e.target.value)} rows={2} data-testid="grn-notes" className="w-full bg-uf-bg border border-uf-border px-3 py-2 text-white rounded mb-4 text-sm" />}
            <div className="space-y-2">
              <div className={`grid ${mode === 'receive' ? 'grid-cols-12' : 'grid-cols-10'} gap-2 px-2 py-2 text-xs uppercase tracking-wider text-uf-textMuted bg-uf-bg rounded`}>
                <div className={mode === 'receive' ? 'col-span-4' : 'col-span-4'}>Product</div>
                <div className="col-span-1 text-right">Ordered</div>
                <div className="col-span-1 text-right">Received</div>
                <div className="col-span-1 text-right">Cost</div>
                <div className="col-span-1 text-right">Line</div>
                <div className="col-span-2">Status</div>
                {mode === 'receive' && <div className="col-span-2">Discrepancy</div>}
              </div>
              {po.lines?.map(l => {
                const gl = grnLines[l.id] || { qty: 0, discrepancy_type: 'correct', discrepancy_reason: '' };
                return (
                  <div key={l.id} className={`grid ${mode === 'receive' ? 'grid-cols-12' : 'grid-cols-10'} gap-2 items-center px-2 py-2 border-b border-uf-border text-sm`}>
                    <div className={mode === 'receive' ? 'col-span-4' : 'col-span-4'}><div className="text-white flex items-center gap-2"><Package className="w-3 h-3 text-uf-textMuted" />{l.product_name}</div><div className="text-uf-textMuted text-xs font-mono">{l.product_sku}</div></div>
                    <div className="col-span-1 text-right text-white">{l.qty_ordered}</div>
                    {mode === 'receive' && l.status === 'open' ? (
                      <input type="number" min="0" max={l.qty_outstanding} value={gl.qty}
                             onChange={e => setGrnLines(g => ({ ...g, [l.id]: { ...gl, qty: e.target.value } }))}
                             data-testid={`grn-qty-${l.id}`}
                             className="col-span-1 bg-uf-bg border border-uf-border px-2 py-1 text-white rounded text-right" />
                    ) : (
                      <div className="col-span-1 text-right text-white">{l.qty_received}</div>
                    )}
                    <div className="col-span-1 text-right text-uf-textMuted">£{l.unit_cost?.toFixed(2)}</div>
                    <div className="col-span-1 text-right text-white">£{l.line_total?.toFixed(2)}</div>
                    <div className="col-span-2"><span className={`text-[10px] px-2 py-0.5 rounded ${STATUS_STYLES[l.status === 'open' ? 'draft' : l.status] || ''}`}>{l.status}</span></div>
                    {mode === 'receive' && l.status === 'open' && (
                      <div className="col-span-2 space-y-1">
                        <select value={gl.discrepancy_type} onChange={e => setGrnLines(g => ({ ...g, [l.id]: { ...gl, discrepancy_type: e.target.value } }))} data-testid={`grn-disc-${l.id}`} className="w-full bg-uf-bg border border-uf-border px-2 py-1 text-white text-xs rounded">
                          <option value="correct">As ordered</option>
                          <option value="short">Short</option>
                          <option value="damaged">Damaged</option>
                          <option value="over">Over-delivered</option>
                        </select>
                        {gl.discrepancy_type !== 'correct' && <input placeholder="Reason…" value={gl.discrepancy_reason} onChange={e => setGrnLines(g => ({ ...g, [l.id]: { ...gl, discrepancy_reason: e.target.value } }))} data-testid={`grn-reason-${l.id}`} className="w-full bg-uf-bg border border-uf-border px-2 py-1 text-white text-xs rounded" />}
                      </div>
                    )}
                    {mode === 'receive' && l.status !== 'open' && <div className="col-span-2 text-uf-textMuted text-xs">closed</div>}
                  </div>
                );
              })}
            </div>
            {mode === 'receive' && (
              <div className="flex gap-2 mt-4 justify-end">
                <button onClick={() => setMode('view')} className="px-4 py-2 border border-uf-border text-white rounded text-sm">Cancel</button>
                <button onClick={postGRN} data-testid="grn-post-btn" className="px-4 py-2 bg-green-600 text-white rounded text-sm flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Post GRN</button>
              </div>
            )}
          </div>

          {/* GRNs */}
          {po.grns?.length > 0 && mode === 'view' && (
            <div className="bg-uf-surface border border-uf-border rounded p-6 mt-4">
              <h3 className="text-white font-bold mb-3">Goods Received Notes</h3>
              {po.grns.map(g => <div key={g.id} className="flex justify-between py-2 border-b border-uf-border text-sm">
                <div className="text-white font-mono">{g.grn_number}</div>
                <div className="text-uf-textMuted">{g.received_date}</div>
                <div className="text-white">£{g.total_value?.toFixed(2)}</div>
              </div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
